import React, { useMemo } from "react";
import { iconsaxPlay, iconsaxPause } from "@wbly/tokens/icon";
import { Cloudinary } from "@cloudinary/url-gen";
import { AdvancedVideo } from "@cloudinary/react";
import { Icon } from "../Icon";
import { useVideoPlayer } from "./useVideoPlayer";

import styles from "./VideoPlayer.module.css";

export type VideoProps = {
  className?: string;
  videoTitle: string;
  cloudinaryName: string;
  cldPublicId: string;
  height?: number;
  width?: number;
  videoType: string;
} & Partial<JSX.IntrinsicElements["video"]>;

export const VideoPlayer = ({
  className,
  videoTitle,
  cldPublicId,
  cloudinaryName,
  videoType,
}: VideoProps) => {
  const { handleClick, videoEl, isPlaying, setIsPlaying } = useVideoPlayer();

  const cld = useMemo(
    () =>
      new Cloudinary({
        cloud: {
          cloudName: `${cloudinaryName}`,
        },
      }),
    [cloudinaryName]
  );

  const cldVid = useMemo(() => cld.video(cldPublicId), [cld, cldPublicId]);

  const VIDEO_ATTRIBUTES = {
    muted: true,
    autoPlay: true,
    playsInline: true,
    loop: true,
    controls: false,
  };

  return (
    <div className={`${styles.container} ${className}`}>
      <AdvancedVideo
        className={`${styles.video} ${className}`}
        ref={videoEl}
        aria-label={videoTitle}
        data-testid="video-player"
        data-video-type={videoType}
        data-video-slug={videoTitle}
        data-is-playing={isPlaying ? "playing" : "paused"}
        cldVid={cldVid}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        {...VIDEO_ATTRIBUTES}
      />
      <button
        className={`${styles.videoButton} ${className}`}
        onClick={handleClick}
      >
        {isPlaying ? <Icon href={iconsaxPause} /> : <Icon href={iconsaxPlay} />}
      </button>
    </div>
  );
};

VideoPlayer.displayName = "VideoPlayer";
